let zptCartEventPromise;

const getZptCartEvent = () => {
  zptCartEventPromise ??=
    import('@shopify/events')
      .then(
        ({ CartLinesUpdateEvent }) =>
          CartLinesUpdateEvent
      )
      .catch(() => null);

  return zptCartEventPromise;
};

const nextPaint = () =>
  new Promise(resolve => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  });

class ZptStorefrontCart {
  constructor(config) {
    this.config = config;

    this.mode =
      String(
        config.mode || ''
      ).trim();

    this.cartKey =
      String(
        config.cartKey || ''
      ).trim();

    this.storefrontKey =
      String(
        config.storefrontKey || ''
      ).trim();

    this.productHandle =
      String(
        config.productHandle || ''
      ).trim();

    this.previewRouteHandle =
      String(
        config.previewRouteHandle || ''
      ).trim();

    this.allowedVariantId =
      Number(
        config.allowedVariantId
      ) || 0;

    this.cookieDomain =
      String(
        config.cookieDomain || ''
      )
        .trim()
        .toLowerCase();

    this.cookieName =
      `zpt_cart_${this.safeKey(
        this.cartKey
      )}`;

    this.syncing = false;
    this.initialized = false;
    this.saveTimer = null;
  }

  safeKey(value) {
    return String(value)
      .toLowerCase()
      .replace(
        /[^a-z0-9_-]/g,
        '_'
      );
  }

  getRoot() {
    return (
      window.Shopify?.routes
        ?.root ||
      '/'
    );
  }

  getCookie(name) {
    const prefix =
      `${encodeURIComponent(
        name
      )}=`;

    const cookies =
      document.cookie.split(';');

    for (
      const rawCookie of cookies
    ) {
      const cookie =
        rawCookie.trim();

      if (
        cookie.startsWith(
          prefix
        )
      ) {
        try {
          return decodeURIComponent(
            cookie.slice(
              prefix.length
            )
          );
        } catch {
          return '';
        }
      }
    }

    return '';
  }

  setCookie(name, value) {
    const parts = [
      `${encodeURIComponent(
        name
      )}=${encodeURIComponent(
        value
      )}`,
      'Path=/',
      'Max-Age=2592000',
      'SameSite=Lax'
    ];

    if (
      location.protocol ===
      'https:'
    ) {
      parts.push('Secure');
    }

    if (this.cookieDomain) {
      parts.push(
        `Domain=${this.cookieDomain}`
      );
    }

    document.cookie =
      parts.join('; ');
  }

  readSnapshot() {
    const raw =
      this.getCookie(
        this.cookieName
      );

    if (!raw) {
      return null;
    }

    try {
      const parsed =
        JSON.parse(raw);

      if (
        !parsed ||
        parsed.v !== 1 ||
        !Array.isArray(parsed.l)
      ) {
        return null;
      }

      return {
        version: 1,

        lines:
          parsed.l
            .map(line => ({
              id:
                Number(line[0]),

              quantity:
                Number(line[1])
            }))
            .filter(
              line =>
                line.id > 0 &&
                line.quantity > 0
            )
      };
    } catch {
      return null;
    }
  }

  isAllowedStandaloneVariant(
    variantId
  ) {
    if (
      this.mode !==
      'standalone'
    ) {
      return true;
    }

    /*
     * The standalone cart belongs to one
     * product, but that product can still
     * have multiple variants.
     *
     * The cart manager therefore does not
     * reject variants solely because they
     * differ from the initially selected
     * variant.
     *
     * Product isolation is enforced when
     * saving/restoring by the logical
     * standalone cart key and hostname.
     */
    return Number(variantId) > 0;
  }

  getSnapshotLines(cart) {
    if (
      !cart ||
      !Array.isArray(cart.items)
    ) {
      return [];
    }

    return cart.items
      .map(item => ({
        id:
          Number(
            item.variant_id ||
            item.id
          ),

        quantity:
          Number(
            item.quantity
          )
      }))
      .filter(
        line =>
          line.id > 0 &&
          line.quantity > 0 &&
          this
            .isAllowedStandaloneVariant(
              line.id
            )
      );
  }

  writeSnapshot(cart) {
    const lines =
      this.getSnapshotLines(
        cart
      )
        .map(line => [
          line.id,
          line.quantity
        ]);

    const snapshot = {
      v: 1,
      l: lines
    };

    this.setCookie(
      this.cookieName,
      JSON.stringify(
        snapshot
      )
    );
  }

  async fetchCart() {
    const response =
      await fetch(
        `${this.getRoot()}cart.js`,
        {
          method: 'GET',

          headers: {
            Accept:
              'application/json'
          },

          credentials:
            'same-origin',

          cache:
            'no-store'
        }
      );

    if (!response.ok) {
      throw new Error(
        'Could not read Shopify cart.'
      );
    }

    return response.json();
  }

  async syncPreviewContext(cart) {
    const isPreviewStorefront =
      this.mode === 'preview-storefront' && Boolean(this.storefrontKey);
    if (!isPreviewStorefront) {
      return cart;
    }

    const attributes = {
      ...(cart?.attributes || {})
    };

    const nextStorefront = this.storefrontKey;
    const nextRoute = this.previewRouteHandle;

    if (
      attributes._storefront === nextStorefront &&
      attributes._zpt_preview_route === nextRoute
    ) {
      return cart;
    }

    const response = await fetch(
      `${this.getRoot()}cart/update.js`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          attributes: {
            ...attributes,
            _storefront: nextStorefront,
            _zpt_preview_route: nextRoute
          }
        })
      }
    );

    if (!response.ok) {
      throw new Error(
        'Could not save preview storefront context.'
      );
    }

    return response.json();
  }

  normalizeCart(cart) {
    const quantities =
      new Map();

    for (
      const item of
      cart?.items || []
    ) {
      const id =
        Number(
          item.variant_id ||
          item.id
        );

      const quantity =
        Number(
          item.quantity
        );

      if (
        !id ||
        quantity < 1
      ) {
        continue;
      }

      if (
        !this
          .isAllowedStandaloneVariant(
            id
          )
      ) {
        continue;
      }

      quantities.set(
        id,
        (
          quantities.get(id) ||
          0
        ) + quantity
      );
    }

    return [
      ...quantities.entries()
    ]
      .map(
        ([id, quantity]) => ({
          id,
          quantity
        })
      )
      .sort(
        (a, b) =>
          a.id - b.id
      );
  }

  normalizeSnapshot(
    snapshot
  ) {
    const quantities =
      new Map();

    for (
      const line of
      snapshot?.lines || []
    ) {
      const id =
        Number(line.id);

      const quantity =
        Number(
          line.quantity
        );

      if (
        !id ||
        quantity < 1
      ) {
        continue;
      }

      if (
        !this
          .isAllowedStandaloneVariant(
            id
          )
      ) {
        continue;
      }

      quantities.set(
        id,
        (
          quantities.get(id) ||
          0
        ) + quantity
      );
    }

    return [
      ...quantities.entries()
    ]
      .map(
        ([id, quantity]) => ({
          id,
          quantity
        })
      )
      .sort(
        (a, b) =>
          a.id - b.id
      );
  }

  cartsMatch(
    cart,
    snapshot
  ) {
    const current =
      this.normalizeCart(
        cart
      );

    const desired =
      this.normalizeSnapshot(
        snapshot
      );

    /*
     * For standalone products we must
     * compare the TOTAL Shopify cart too.
     *
     * Otherwise an unrelated product could
     * exist in the native cart while the
     * normalized standalone portion still
     * appears to match.
     */
    if (
      this.mode === 'standalone' ||
      this.mode.startsWith('preview-')
    ) {
      const totalCurrentLines =
        (
          cart?.items || []
        ).filter(
          item =>
            Number(
              item.quantity
            ) > 0
        ).length;

      if (
        totalCurrentLines !==
        desired.length
      ) {
        return false;
      }
    }

    if (
      current.length !==
      desired.length
    ) {
      return false;
    }

    return current.every(
      (line, index) =>
        line.id ===
          desired[index].id &&
        line.quantity ===
          desired[index]
            .quantity
    );
  }

  getSectionIds() {
    const ids = [
      ...document.querySelectorAll(
        'cart-items-component'
      )
    ]
      .map(
        element =>
          element.dataset
            .sectionId
      )
      .filter(Boolean);

    ids.push(
      'zpt-cart-drawer-section'
    );

    return [
      ...new Set(ids)
    ];
  }

  async clearCart() {
    const response =
      await fetch(
        `${this.getRoot()}cart/clear.js`,
        {
          method: 'POST',

          headers: {
            'Content-Type':
              'application/json',

            Accept:
              'application/json'
          },

          credentials:
            'same-origin',

          body:
            JSON.stringify({})
        }
      );

    if (!response.ok) {
      throw new Error(
        'Could not clear Shopify cart.'
      );
    }

    return response.json();
  }

  async addLines(lines) {
    if (!lines.length) {
      return null;
    }

    const response =
      await fetch(
        `${this.getRoot()}cart/add.js`,
        {
          method: 'POST',

          headers: {
            'Content-Type':
              'application/json',

            Accept:
              'application/json'
          },

          credentials:
            'same-origin',

          body:
            JSON.stringify({
              items:
                lines.map(
                  line => ({
                    id:
                      Number(
                        line.id
                      ),

                    quantity:
                      Number(
                        line.quantity
                      )
                  })
                ),

              sections:
                this
                  .getSectionIds()
                  .join(',')
            })
        }
      );

    const result =
      await response.json();

    if (
      !response.ok ||
      result?.status
    ) {
      throw new Error(
        result?.description ||
        result?.message ||
        'Could not restore cart.'
      );
    }

    return result;
  }

  async getFreshSections() {
    const ids =
      this.getSectionIds();

    if (!ids.length) {
      return {};
    }

    const url =
      new URL(
        this.getRoot(),
        location.origin
      );

    url.searchParams.set(
      'sections',
      ids.join(',')
    );

    const response =
      await fetch(
        url.toString(),
        {
          method: 'GET',

          headers: {
            Accept:
              'application/json'
          },

          credentials:
            'same-origin',

          cache:
            'no-store'
        }
      );

    if (!response.ok) {
      return {};
    }

    return response.json();
  }

  async notifyHorizon(
    cart,
    sections = {}
  ) {
    const CartLinesUpdateEvent =
      await getZptCartEvent();

    if (!CartLinesUpdateEvent) {
      return;
    }

    const deferred =
      CartLinesUpdateEvent
        .createPromise();

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',

        context:
          'zpt-storefront-cart',

        lines:
          cart.items.map(
            item => ({
              merchandiseId:
                String(
                  item.variant_id ||
                  item.id
                ),

              quantity:
                item.quantity
            })
          ),

        promise:
          deferred.promise
      })
    );

    deferred.resolve({
      cart:
        CartLinesUpdateEvent
          .createCartFromAjaxResponse(
            cart
          ),

      detail: {
        items:
          cart.items,

        sections,

        source:
          'zpt-storefront-cart',

        sourceId:
          this.cartKey,

        itemCount:
          cart.item_count,

        didError:
          false
      }
    });

    await nextPaint();
  }

  async restoreSnapshot(
    snapshot
  ) {
    this.syncing = true;

    try {
      await this.clearCart();

      let result = null;

      const lines =
        this.normalizeSnapshot(
          snapshot
        );

      if (lines.length) {
        result =
          await this.addLines(
            lines
          );
      }

      const cart =
        await this.syncPreviewContext(
          await this.fetchCart()
        );

      let sections =
        result?.sections ||
        {};

      if (
        !Object.keys(
          sections
        ).length
      ) {
        sections =
          await this
            .getFreshSections();
      }

      this.writeSnapshot(
        cart
      );

      await this.notifyHorizon(
        cart,
        sections
      );

      window.dispatchEvent(
        new CustomEvent(
          'zpt:storefront-cart-ready',
          {
            detail: {
              mode:
                this.mode,

              cartKey:
                this.cartKey,

              storefrontKey:
                this.storefrontKey,

              productHandle:
                this.productHandle,

              cart
            }
          }
        )
      );

      return cart;
    } finally {
      this.syncing =
        false;
    }
  }

  async saveCurrentCart() {
    if (this.syncing) {
      return null;
    }

    const cart =
      await this.fetchCart();

    this.writeSnapshot(
      cart
    );

    window.dispatchEvent(
      new CustomEvent(
        'zpt:storefront-cart-saved',
        {
          detail: {
            mode:
              this.mode,

            cartKey:
              this.cartKey,

            storefrontKey:
              this.storefrontKey,

            productHandle:
              this.productHandle,

            cart
          }
        }
      )
    );

    return cart;
  }

  scheduleSave() {
    clearTimeout(
      this.saveTimer
    );

    this.saveTimer =
      setTimeout(
        () => {
          this
            .saveCurrentCart()
            .catch(error => {
              console.error(
                'ZPT Cart:',
                error
              );
            });
        },
        250
      );
  }

  dispatchReady(cart) {
    window.dispatchEvent(
      new CustomEvent(
        'zpt:storefront-cart-ready',
        {
          detail: {
            mode:
              this.mode,

            cartKey:
              this.cartKey,

            storefrontKey:
              this.storefrontKey,

            productHandle:
              this.productHandle,

            cart
          }
        }
      )
    );
  }

  async initialize() {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.syncing = true;

    try {
      const currentCart =
        await this.syncPreviewContext(
          await this.fetchCart()
        );

      const snapshot =
        this.readSnapshot();

      /*
       * STOREFRONT
       *
       * Preserve the behavior that was
       * already working:
       *
       * first visit -> adopt native cart
       * later visits -> restore shared
       * storefront cart when required.
       */
      if (
        this.mode ===
        'storefront'
      ) {
        if (!snapshot) {
          this.writeSnapshot(
            currentCart
          );

          this.dispatchReady(
            currentCart
          );

          return;
        }

        if (
          this.cartsMatch(
            currentCart,
            snapshot
          )
        ) {
          this.dispatchReady(
            currentCart
          );

          return;
        }

        this.syncing = false;

        await this.restoreSnapshot(
          snapshot
        );

        return;
      }

      /*
       * STANDALONE
       *
       * A standalone Product Route gets
       * its own logical cart.
       *
       * If it has no saved cart yet,
       * start clean instead of adopting
       * whatever Shopify cart happens to
       * exist on the hostname.
       */
      if (
        this.mode === 'standalone' ||
        this.mode.startsWith('preview-')
      ) {
        if (!snapshot) {
          if (
            currentCart.item_count >
            0
          ) {
            await this.clearCart();

            const cleanCart =
              await this.syncPreviewContext(
                await this.fetchCart()
              );

            const sections =
              await this
                .getFreshSections();

            this.writeSnapshot(
              cleanCart
            );

            await this.notifyHorizon(
              cleanCart,
              sections
            );

            this.dispatchReady(
              cleanCart
            );

            return;
          }

          this.writeSnapshot(
            currentCart
          );

          this.dispatchReady(
            currentCart
          );

          return;
        }

        if (
          this.cartsMatch(
            currentCart,
            snapshot
          )
        ) {
          this.dispatchReady(
            currentCart
          );

          return;
        }

        this.syncing = false;

        await this.restoreSnapshot(
          snapshot
        );

        return;
      }

      this.dispatchReady(
        currentCart
      );
    } catch (error) {
      console.error(
        'ZPT Cart:',
        error
      );
    } finally {
      this.syncing =
        false;
    }
  }

  bind() {
    window.addEventListener(
      'zpt:cart-mutated',
      () => {
        this.scheduleSave();
      }
    );

    document.addEventListener(
      'change',
      event => {
        if (
          event.target.closest(
            'cart-items-component'
          )
        ) {
          setTimeout(
            () => {
              this.scheduleSave();
            },
            400
          );
        }
      },
      true
    );

    document.addEventListener(
      'click',
      event => {
        if (
          event.target.closest(
            'cart-items-component button'
          )
        ) {
          setTimeout(
            () => {
              this.scheduleSave();
            },
            400
          );
        }
      },
      true
    );

    document.addEventListener(
      'submit',
      event => {
        if (
          event.target.closest(
            'product-form'
          ) ||
          event.target.matches(
            'form[action*="/cart/add"]'
          )
        ) {
          setTimeout(
            () => {
              this.scheduleSave();
            },
            700
          );
        }
      },
      true
    );

    window.addEventListener(
      'pageshow',
      event => {
        if (
          event.persisted
        ) {
          this.initialized =
            false;

          this.initialize();
        }
      }
    );
  }
}

const configElement =
  document.getElementById(
    'zpt-storefront-cart-config'
  );

if (configElement) {
  try {
    const config =
      JSON.parse(
        configElement.textContent ||
        '{}'
      );

    if (
      config.enabled &&
      config.cartKey &&
      config.cookieDomain
    ) {
      const manager =
        new ZptStorefrontCart(
          config
        );

      window.ZptStorefrontCart =
        manager;

      manager.bind();
      manager.initialize();
    }
  } catch (error) {
    console.error(
      'ZPT Cart:',
      error
    );
  }
}
